/**
 * workflowApi — typed client for the M04 Workflow Engine admin surface.
 *
 * All calls target the tenant-safe facade at `v1/wf/*` (reached through the
 * Vite `/api → /v1` proxy). The facade derives BOTH the tenant and the acting
 * user from the authenticated JWT server-side, so — exactly like orgApi /
 * brandingApi — NO tenantId or actorUserId is ever sent from the browser.
 *
 * Two facade controllers back these paths:
 *  - WorkflowFacadeController          → read catalogue (definitions/escalations/sla/instances)
 *  - WorkflowAuthoringFacadeController → authoring + runtime (create/update/lifecycle/actions)
 *
 * List responses are normalised here so screens never crash on `.map` of a
 * non-array: the read facade wraps rows as `{ rows, total }`, some endpoints
 * return bare arrays, and a couple use `{ data, total }`.
 */
import { apiRequest } from './client.js';
import type { ApprovalTrailStep } from '../components/ApprovalLevels.js';

type Tok = string | null | undefined;

/** Normalise a list response to `{ rows, total }` regardless of backend shape. */
export async function getRows<T>(path: string, token: Tok): Promise<{ rows: T[]; total: number }> {
  const res = await apiRequest<unknown>(path, { token });
  if (Array.isArray(res)) return { rows: res as T[], total: res.length };
  const obj = (res ?? {}) as { rows?: T[]; data?: T[]; total?: number };
  const rows = Array.isArray(obj.rows) ? obj.rows : Array.isArray(obj.data) ? obj.data : [];
  return { rows, total: typeof obj.total === 'number' ? obj.total : rows.length };
}

export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// ── Enum value sets (mirror backend enums/workflow.enums.ts) ──────────────────
export const WORKFLOW_STATUSES = ['DRAFT', 'ACTIVE', 'DEPRECATED', 'ARCHIVED'] as const;
export const WORKFLOW_STEP_TYPES = [
  'APPROVAL', 'REVIEW', 'NOTIFICATION', 'SYSTEM_ACTION', 'PARALLEL_GATE', 'CONDITIONAL',
] as const;
export const APPROVER_TYPES = [
  'SPECIFIC_USER', 'ROLE_BASED', 'HIERARCHY', 'TERRITORY_HEAD', 'DYNAMIC_RULE', 'INITIATOR',
] as const;
export const TAT_UNITS = ['CALENDAR_DAYS', 'WORKING_DAYS', 'HOURS'] as const;
export const ESCALATION_TRIGGERS = ['TAT_BREACH', 'TAT_WARNING', 'MANUAL'] as const;
export const ESCALATION_ACTIONS = [
  'NOTIFY_MANAGER', 'AUTO_ESCALATE', 'AUTO_APPROVE', 'AUTO_REJECT',
] as const;
export const INSTANCE_STATUSES = [
  'DRAFT', 'PENDING', 'IN_PROGRESS', 'APPROVED', 'REJECTED', 'RETURNED', 'RECALLED',
  'CANCELLED', 'AUTO_CANCELLED', 'ON_HOLD', 'DELEGATED', 'ESCALATED', 'TIMEOUT',
  'COMPLETED', 'ARCHIVED',
] as const;
export const CONDITION_OPERATORS = [
  'EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN', 'NOT_IN', 'CONTAINS', 'STARTS_WITH',
  'ENDS_WITH', 'IS_NULL', 'IS_NOT_NULL',
] as const;
export const LOGICAL_CONJUNCTIONS = ['AND', 'OR'] as const;
export const DELEGATION_SCOPES = ['INSTANCE', 'GLOBAL'] as const;

export type WorkflowStatus = (typeof WORKFLOW_STATUSES)[number];
export type WorkflowStepType = (typeof WORKFLOW_STEP_TYPES)[number];
export type ApproverType = (typeof APPROVER_TYPES)[number];
export type TatUnit = (typeof TAT_UNITS)[number];
export type EscalationTrigger = (typeof ESCALATION_TRIGGERS)[number];
export type EscalationAction = (typeof ESCALATION_ACTIONS)[number];
export type InstanceStatus = (typeof INSTANCE_STATUSES)[number];
export type ConditionOperator = (typeof CONDITION_OPERATORS)[number];
export type LogicalConjunction = (typeof LOGICAL_CONJUNCTIONS)[number];
export type DelegationScope = (typeof DELEGATION_SCOPES)[number];

// ══════════════════════════════════════════════════════════════════════════
// DEFINITIONS (T045)
// ══════════════════════════════════════════════════════════════════════════
export interface WorkflowStepLite {
  readonly stepId: string;
  readonly stepSequence: number;
  readonly stepName: string;
  readonly stepType: WorkflowStepType;
  readonly approverType?: string | null;
  readonly approverRoleCode?: string | null;
  readonly approverUserId?: string | null;
  readonly isMandatory: boolean;
  readonly allowDelegate: boolean;
  readonly allowReassign: boolean;
  readonly conditions?: StepCondition[];
  readonly approverRules?: ApproverRule[];
  readonly tatConfigurations?: TatConfig[];
}
export interface WorkflowDefinition {
  readonly workflowDefinitionId: string;
  readonly workflowCode: string;
  readonly workflowName: string;
  readonly workflowDescription?: string | null;
  readonly entityType: string;
  readonly moduleCode: string;
  readonly workflowStatus: WorkflowStatus;
  readonly isParallelAllowed: boolean;
  readonly maxApprovalLevels: number;
  readonly autoCancellationHours?: number | null;
  readonly allowsRecall: boolean;
  readonly recallWindowHours?: number | null;
  readonly effectiveFrom?: string | null;
  readonly effectiveTo?: string | null;
  readonly templateRefId?: string | null;
  readonly versionNumber: number;
  readonly recordStatusCode: string;
  readonly steps?: WorkflowStepLite[];
}
export interface CreateDefinitionInput {
  workflowCode: string;
  workflowName: string;
  workflowDescription?: string;
  entityType: string;
  moduleCode: string;
  isParallelAllowed?: boolean;
  maxApprovalLevels?: number;
  autoCancellationHours?: number;
  allowsRecall?: boolean;
  recallWindowHours?: number;
  effectiveFrom?: string;
  effectiveTo?: string;
}
export interface ValidationResult {
  readonly valid: boolean;
  readonly errors: string[];
}

export const listDefinitions = (token: Tok, params: { entityType?: string; status?: string } = {}) =>
  getRows<WorkflowDefinition>(`/v1/wf/definitions${qs(params)}`, token);
export const getDefinition = (token: Tok, id: string) =>
  apiRequest<WorkflowDefinition>(`/v1/wf/definitions/${id}`, { token });
export const createDefinition = (token: Tok, body: CreateDefinitionInput) =>
  apiRequest<WorkflowDefinition>('/v1/wf/definitions', { method: 'POST', token, body });
export const updateDefinition = (token: Tok, id: string, body: Partial<CreateDefinitionInput>) =>
  apiRequest<WorkflowDefinition>(`/v1/wf/definitions/${id}`, { method: 'PATCH', token, body });
export const activateDefinition = (token: Tok, id: string) =>
  apiRequest<WorkflowDefinition | { errors: string[] }>(`/v1/wf/definitions/${id}/activate`, {
    method: 'POST', token,
  });
export const deprecateDefinition = (token: Tok, id: string) =>
  apiRequest<{ success: boolean }>(`/v1/wf/definitions/${id}/deprecate`, { method: 'POST', token });
export const deactivateDefinition = (token: Tok, id: string) =>
  apiRequest<{ success: boolean }>(`/v1/wf/definitions/${id}/deactivate`, { method: 'POST', token });
export const deleteDefinition = (token: Tok, id: string) =>
  apiRequest<void>(`/v1/wf/definitions/${id}`, { method: 'DELETE', token });
export const validateDefinition = (token: Tok, id: string) =>
  apiRequest<ValidationResult>(`/v1/wf/definitions/${id}/validate`, { method: 'POST', token });
export const previewDefinition = (token: Tok, id: string) =>
  apiRequest<unknown>(`/v1/wf/definitions/${id}/preview`, { token });
export const cloneDefinition = (
  token: Tok, id: string, body: { newWorkflowCode: string; newWorkflowName?: string },
) => apiRequest<WorkflowDefinition>(`/v1/wf/definitions/${id}/clone`, { method: 'POST', token, body });

// ══════════════════════════════════════════════════════════════════════════
// STEPS (T046)
// ══════════════════════════════════════════════════════════════════════════
export interface WorkflowStep extends WorkflowStepLite {
  readonly workflowDefinitionId: string;
  readonly stepDescription?: string | null;
  readonly hierarchyLevelOffset?: number | null;
  readonly parallelGroupId?: string | null;
  readonly parallelApprovalThreshold?: number | null;
  readonly onRejectionAction?: string | null;
  readonly rejectionGotoStep?: number | null;
  readonly alternateWorkflowCode?: string | null;
}
export interface CreateStepInput {
  stepSequence: number;
  stepName: string;
  stepDescription?: string;
  stepType: WorkflowStepType;
  approverType?: ApproverType;
  approverRoleCode?: string;
  approverUserId?: string;
  hierarchyLevelOffset?: number;
  isMandatory?: boolean;
  allowDelegate?: boolean;
  allowReassign?: boolean;
  parallelGroupId?: string;
  parallelApprovalThreshold?: number;
  onRejectionAction?: string;
  rejectionGotoStep?: number;
}

export const listSteps = (token: Tok, definitionId: string) =>
  apiRequest<WorkflowStep[]>(`/v1/wf/definitions/${definitionId}/steps`, { token });
export const createStep = (token: Tok, definitionId: string, body: CreateStepInput) =>
  apiRequest<WorkflowStep>(`/v1/wf/definitions/${definitionId}/steps`, { method: 'POST', token, body });
export const getStep = (token: Tok, stepId: string) =>
  apiRequest<WorkflowStep>(`/v1/wf/steps/${stepId}`, { token });
export const updateStep = (token: Tok, stepId: string, body: Partial<CreateStepInput>) =>
  apiRequest<WorkflowStep>(`/v1/wf/steps/${stepId}`, { method: 'PATCH', token, body });
export const deleteStep = (token: Tok, stepId: string) =>
  apiRequest<void>(`/v1/wf/steps/${stepId}`, { method: 'DELETE', token });
export const reorderSteps = (
  token: Tok, definitionId: string, order: Array<{ stepId: string; stepSequence: number }>,
) => apiRequest<{ success: boolean }>(`/v1/wf/definitions/${definitionId}/steps/reorder`, {
  method: 'POST', token, body: { order },
});

// ══════════════════════════════════════════════════════════════════════════
// STEP CONDITIONS (T047)
// ══════════════════════════════════════════════════════════════════════════
export interface StepCondition {
  readonly conditionId: string;
  readonly stepId: string;
  readonly conditionGroup: number;
  readonly fieldPath: string;
  readonly operator: ConditionOperator;
  readonly comparisonValue?: string | null;
  readonly logicalConjunction: LogicalConjunction;
  readonly conditionSequence: number;
  readonly isSkipCondition: boolean;
  readonly recordStatusCode: string;
}
export interface CreateConditionInput {
  conditionGroup?: number;
  fieldPath: string;
  operator: ConditionOperator;
  comparisonValue?: string;
  logicalConjunction?: LogicalConjunction;
  conditionSequence?: number;
  isSkipCondition?: boolean;
}

export const listConditions = (token: Tok, stepId: string) =>
  apiRequest<StepCondition[]>(`/v1/wf/steps/${stepId}/conditions`, { token });
export const createCondition = (token: Tok, stepId: string, body: CreateConditionInput) =>
  apiRequest<StepCondition>(`/v1/wf/steps/${stepId}/conditions`, { method: 'POST', token, body });
export const updateCondition = (token: Tok, conditionId: string, body: Partial<CreateConditionInput>) =>
  apiRequest<StepCondition>(`/v1/wf/conditions/${conditionId}`, { method: 'PATCH', token, body });
export const deleteCondition = (token: Tok, conditionId: string) =>
  apiRequest<void>(`/v1/wf/conditions/${conditionId}`, { method: 'DELETE', token });

// ══════════════════════════════════════════════════════════════════════════
// APPROVER RESOLUTION RULES (T048)
// ══════════════════════════════════════════════════════════════════════════
export interface ApproverRule {
  readonly ruleId: string;
  readonly stepId: string;
  readonly ruleSequence: number;
  readonly approverType: ApproverType;
  readonly conditionField?: string | null;
  readonly conditionValue?: string | null;
  readonly resolvedRoleCode?: string | null;
  readonly resolvedUserId?: string | null;
  readonly hierarchyOffset?: number | null;
  readonly fallbackRuleId?: string | null;
  readonly recordStatusCode: string;
  /** The resolved user, named by the server (G18). */
  readonly actorName?: string | null;
  readonly actorCode?: string | null;
}
export interface CreateApproverRuleInput {
  ruleSequence?: number;
  approverType: ApproverType;
  conditionField?: string;
  conditionValue?: string;
  resolvedRoleCode?: string;
  resolvedUserId?: string;
  hierarchyOffset?: number;
  fallbackRuleId?: string;
}

export const listApproverRules = (token: Tok, stepId: string) =>
  apiRequest<ApproverRule[]>(`/v1/wf/steps/${stepId}/approver-rules`, { token });
export const createApproverRule = (token: Tok, stepId: string, body: CreateApproverRuleInput) =>
  apiRequest<ApproverRule>(`/v1/wf/steps/${stepId}/approver-rules`, { method: 'POST', token, body });
export const updateApproverRule = (token: Tok, ruleId: string, body: Partial<CreateApproverRuleInput>) =>
  apiRequest<ApproverRule>(`/v1/wf/approver-rules/${ruleId}`, { method: 'PATCH', token, body });
export const deleteApproverRule = (token: Tok, ruleId: string) =>
  apiRequest<void>(`/v1/wf/approver-rules/${ruleId}`, { method: 'DELETE', token });

// ══════════════════════════════════════════════════════════════════════════
// TAT CONFIGURATION (T049) — SLA + escalation tiers
// ══════════════════════════════════════════════════════════════════════════
export interface TatConfig {
  readonly tatConfigId: string;
  readonly stepId: string;
  readonly tatValue: number;
  readonly tatUnit: TatUnit;
  readonly warningThresholdPercent: number;
  readonly escalationLevel1Hours?: number | null;
  readonly escalationLevel2Hours?: number | null;
  readonly escalationLevel3Hours?: number | null;
  readonly autoActionOnTimeout?: string | null;
  readonly excludeHolidays: boolean;
  readonly excludeWeekends: boolean;
  readonly effectiveFrom?: string | null;
  readonly effectiveTo?: string | null;
  readonly recordStatusCode: string;
}
export interface CreateTatInput {
  stepId: string;
  tatValue: number;
  tatUnit: TatUnit;
  warningThresholdPercent?: number;
  escalationLevel1Hours?: number;
  escalationLevel2Hours?: number;
  escalationLevel3Hours?: number;
  autoActionOnTimeout?: string;
  excludeHolidays?: boolean;
  excludeWeekends?: boolean;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export const listTat = (token: Tok, stepId?: string) =>
  apiRequest<TatConfig[]>(`/v1/wf/tat-configurations${qs({ stepId })}`, { token });
export const getTat = (token: Tok, id: string) =>
  apiRequest<TatConfig>(`/v1/wf/tat-configurations/${id}`, { token });
export const createTat = (token: Tok, body: CreateTatInput) =>
  apiRequest<TatConfig>('/v1/wf/tat-configurations', { method: 'POST', token, body });
export const updateTat = (token: Tok, id: string, body: Partial<CreateTatInput>) =>
  apiRequest<TatConfig>(`/v1/wf/tat-configurations/${id}`, { method: 'PATCH', token, body });
export const deleteTat = (token: Tok, id: string) =>
  apiRequest<void>(`/v1/wf/tat-configurations/${id}`, { method: 'DELETE', token });

// ── SLA + Escalation read views (from read facade) ───────────────────────────
export interface SlaRow extends TatConfig {
  readonly step?: WorkflowStepLite;
}
export interface EscalationRow {
  readonly escalationId: string;
  readonly instanceId: string;
  readonly escalationLevel: number;
  readonly escalationTrigger: EscalationTrigger;
  readonly escalationAction: EscalationAction;
  readonly escalatedToUserId?: string | null;
  readonly escalatedAt: string;
  readonly isResolved: boolean;
  readonly resolutionRemarks?: string | null;
}
export const listSla = (token: Tok, definitionId?: string) =>
  getRows<SlaRow>(`/v1/wf/sla${qs({ definitionId })}`, token);
export const listEscalations = (token: Tok, definitionId?: string) =>
  getRows<EscalationRow>(`/v1/wf/escalations${qs({ definitionId })}`, token);

// ══════════════════════════════════════════════════════════════════════════
// INSTANCES (T050) — runtime
// ══════════════════════════════════════════════════════════════════════════
export interface ActionLogRow {
  readonly actionLogId: string;
  readonly actionType: string;
  readonly actorUserId: string;
  /** Who acted, named by the server (G18). */
  readonly actorName?: string | null;
  readonly actorCode?: string | null;
  readonly remarks?: string | null;
  readonly actionTimestamp: string;
  readonly fromStatus?: string | null;
  readonly toStatus?: string | null;
}
export interface WorkflowInstance {
  readonly instanceId: string;
  readonly workflowDefinitionId: string;
  readonly instanceRefNumber?: string | null;
  readonly entityType: string;
  readonly entityRefId?: string | null;
  readonly entityRefNumber?: string | null;
  readonly instanceStatus: InstanceStatus;
  readonly currentStepId?: string | null;
  readonly initiatorUserId?: string | null;
  readonly submittedAt?: string | null;
  readonly completedAt?: string | null;
  readonly createdAt?: string | null;
  /** Each level with its step name and its approvers / actor named (GET /v1/wf/instances/:id). */
  readonly stepTrail?: ApprovalTrailStep[];
  readonly actionLogs?: ActionLogRow[];
  readonly escalations?: EscalationRow[];
}
export interface InstanceStatusResult {
  readonly instanceId: string;
  readonly instanceStatus: InstanceStatus;
  readonly currentStepId?: string | null;
  readonly submittedAt?: string | null;
  readonly completedAt?: string | null;
}

export const listInstances = (
  token: Tok,
  params: { status?: string; businessRef?: string; entityType?: string; limit?: number; offset?: number } = {},
) => getRows<WorkflowInstance>(`/v1/wf/instances${qs(params)}`, token);
export const getInstance = (token: Tok, id: string) =>
  apiRequest<WorkflowInstance>(`/v1/wf/instances/${id}`, { token });
export const getInstanceStatus = (token: Tok, id: string) =>
  apiRequest<InstanceStatusResult>(`/v1/wf/instances/${id}/status`, { token });
export const getInstanceHistory = (token: Tok, id: string) =>
  apiRequest<ActionLogRow[]>(`/v1/wf/instances/${id}/history`, { token });
export const submitInstance = (
  token: Tok,
  body: { workflowDefinitionId: string; entityType: string; entityRefId: string; metadata?: Record<string, unknown> },
) => apiRequest<WorkflowInstance>('/v1/wf/instances', { method: 'POST', token, body });

// ── Runtime actions ──────────────────────────────────────────────────────────
type ActionResult = WorkflowInstance | Record<string, unknown>;
const post = <T = ActionResult>(token: Tok, path: string, body?: unknown) =>
  apiRequest<T>(path, { method: 'POST', token, body });

export const approveInstance = (token: Tok, id: string, remarks?: string) =>
  post(token, `/v1/wf/instances/${id}/approve`, { remarks });
export const rejectInstance = (token: Tok, id: string, remarks: string) =>
  post(token, `/v1/wf/instances/${id}/reject`, { remarks });
export const returnInstance = (token: Tok, id: string, remarks: string) =>
  post(token, `/v1/wf/instances/${id}/return`, { remarks });
export const recallInstance = (token: Tok, id: string, remarks?: string) =>
  post(token, `/v1/wf/instances/${id}/recall`, { remarks });
export const holdInstance = (token: Tok, id: string, remarks?: string) =>
  post(token, `/v1/wf/instances/${id}/hold`, { remarks });
export const resumeInstance = (token: Tok, id: string) =>
  post(token, `/v1/wf/instances/${id}/resume`);
export const cancelInstance = (token: Tok, id: string, remarks?: string) =>
  post(token, `/v1/wf/instances/${id}/cancel`, { remarks });
export const delegateInstance = (
  token: Tok, id: string,
  body: { toUserId: string; reason?: string; effectiveFrom?: string; effectiveTo?: string },
) => post(token, `/v1/wf/instances/${id}/delegate`, body);
export const escalateInstance = (
  token: Tok, id: string,
  body: { escalatedToUserId: string; escalationLevel?: number; escalationAction?: EscalationAction; remarks?: string },
) => post(token, `/v1/wf/instances/${id}/escalate`, body);
export const forwardInstance = (token: Tok, id: string, body: { toUserId: string; remarks?: string }) =>
  post(token, `/v1/wf/instances/${id}/forward`, body);
export const skipStep = (token: Tok, id: string, remarks?: string) =>
  post(token, `/v1/wf/instances/${id}/skip`, { remarks });
export const reworkInstance = (
  token: Tok, id: string, body: { remarks: string; goToStepSequence?: number },
) => post(token, `/v1/wf/instances/${id}/rework`, body);

// ══════════════════════════════════════════════════════════════════════════
// SIMULATOR (T057)
// ══════════════════════════════════════════════════════════════════════════
export interface SimulationLog {
  readonly simulationId: string;
  readonly workflowDefinitionId: string;
  readonly simulationStatus: string;
  readonly simulationInput?: Record<string, unknown> | null;
  readonly simulationResult?: Record<string, unknown> | null;
  readonly createdAt: string;
}
export const simulateDefinition = (
  token: Tok, definitionId: string, simulationInput: Record<string, unknown>,
) => apiRequest<unknown>(`/v1/wf/definitions/${definitionId}/simulate`, {
  method: 'POST', token, body: { simulationInput },
});
export const listSimulationLogs = (token: Tok, definitionId: string, limit = 20) =>
  apiRequest<SimulationLog[]>(`/v1/wf/definitions/${definitionId}/simulation-logs${qs({ limit })}`, { token });
export const getSimulationLog = (token: Tok, simulationId: string) =>
  apiRequest<SimulationLog>(`/v1/wf/simulation-logs/${simulationId}`, { token });

// ══════════════════════════════════════════════════════════════════════════
// TEMPLATE LIBRARY (T058)
// ══════════════════════════════════════════════════════════════════════════
export interface WorkflowTemplate {
  readonly templateId: string;
  readonly templateCode: string;
  readonly templateName: string;
  readonly templateDescription?: string | null;
  readonly entityType?: string | null;
  readonly templateJson?: Record<string, unknown> | null;
  readonly isSystemTemplate: boolean;
  readonly usageCount: number;
  readonly recordStatusCode: string;
}
export const listTemplates = (token: Tok, entityType?: string) =>
  apiRequest<WorkflowTemplate[]>(`/v1/wf/templates${qs({ entityType })}`, { token });
export const getTemplate = (token: Tok, id: string) =>
  apiRequest<WorkflowTemplate>(`/v1/wf/templates/${id}`, { token });
export const instantiateTemplate = (
  token: Tok, id: string, body: { workflowCode: string; workflowName: string },
) => apiRequest<{ templateId: string; templateCode: string; workflowCode: string; workflowName: string; templateJson: unknown; message: string }>(
  `/v1/wf/templates/${id}/instantiate`, { method: 'POST', token, body },
);
export const cloneTemplate = (
  token: Tok, id: string, body: { newTemplateCode: string; newTemplateName: string },
) => apiRequest<WorkflowTemplate>(`/v1/wf/templates/${id}/clone`, { method: 'POST', token, body });

// ══════════════════════════════════════════════════════════════════════════
// DELEGATIONS (T054)
// ══════════════════════════════════════════════════════════════════════════
export interface Delegation {
  readonly delegationId: string;
  readonly delegatorUserId: string;
  readonly delegateeUserId: string;
  readonly delegationScope: DelegationScope;
  readonly instanceId?: string | null;
  readonly effectiveFrom: string;
  readonly effectiveTo: string;
  readonly delegationStatus: string;
  readonly reason?: string | null;
  /** The delegatee, named by the server (G18). */
  readonly actorName?: string | null;
  readonly actorCode?: string | null;
}
export interface CreateDelegationInput {
  delegateeUserId: string;
  effectiveFrom: string;
  effectiveTo: string;
  scope: DelegationScope;
  instanceId?: string;
  reason?: string;
}
export const createDelegation = (token: Tok, body: CreateDelegationInput) =>
  apiRequest<Delegation>('/v1/wf/delegations', { method: 'POST', token, body });
export const listDelegations = (
  token: Tok, params: { delegatorUserId?: string; delegateeUserId?: string; status?: string } = {},
) => apiRequest<Delegation[]>(`/v1/wf/delegations${qs(params)}`, { token });
export const getActiveDelegation = (token: Tok, userId: string) =>
  apiRequest<Delegation | null>(`/v1/wf/delegations/active/${userId}`, { token });
export const revokeDelegation = (token: Tok, id: string) =>
  apiRequest<void>(`/v1/wf/delegations/${id}`, { method: 'DELETE', token });

// ══════════════════════════════════════════════════════════════════════════
// DASHBOARD + ANALYTICS
// ══════════════════════════════════════════════════════════════════════════
export interface DashboardData {
  readonly [key: string]: unknown;
}
export const getDashboard = (token: Tok) =>
  apiRequest<{ success: boolean; data: DashboardData }>(`/v1/wf/dashboard`, { token });
export const getAnalytics = (
  token: Tok,
  params: { workflowId?: string; dateFrom?: string; dateTo?: string; groupBy?: 'day' | 'week' | 'month' } = {},
) => apiRequest<{ success: boolean; data: unknown }>(`/v1/wf/analytics${qs(params)}`, { token });
export const getMyPendingApprovals = (
  token: Tok, params: { page?: number; pageSize?: number; overdueOnly?: boolean } = {},
) => apiRequest<{ success: boolean; data: unknown[]; meta: { page: number; pageSize: number; total: number } }>(
  `/v1/wf/my-pending-approvals${qs(params)}`, { token },
);

/** Row shape returned by both approval queues. */
export interface PendingApproval {
  readonly instanceStepId: string;
  readonly instanceId: string;
  readonly stepSequence: number;
  readonly assignedUserId: string | null;
  readonly assignedUserName: string | null;
  readonly assignedLoginId: string | null;
  readonly assignedRoleCode: string | null;
  readonly assignedAt: string | null;
  readonly dueAt: string | null;
  readonly isOverdue: boolean;
  readonly instanceRefNumber: string | null;
  readonly entityType: string | null;
  readonly entityRefId: string | null;
  readonly initiatorUserId: string | null;
  readonly submittedAt: string | null;
  readonly priorityLevel: number | null;
  /**
   * All Approvals only: whether this reader may decide the step himself, by
   * the engine's own guard. Overseeing a request is not holding it.
   */
  readonly canAct?: boolean;
}

/**
 * Every pending approval in the tenant, not just the caller's own queue.
 * Lives on the analytics controller (`@Controller('v1')`), hence no `/wf`.
 */
export const getAllPendingApprovals = (
  token: Tok,
  params: { page?: number; pageSize?: number; overdueOnly?: boolean } = {},
) =>
  apiRequest<{
    success: boolean;
    data: PendingApproval[];
    meta: { page: number; pageSize: number; total: number };
  }>(`/v1/all-pending-approvals${qs(params)}`, { token });
